import { AxiosProgressEvent } from 'axios';
import { useAuth } from '@/features/auth';
import { useNotificationContext } from '@/features/notifications';
import { api } from '@/shared/api';
import { renameFile } from '@/shared/utils';
import { captureError } from '@/shared/utils/error-reporting';

type UploadFileArgs = {
  procId?: string | null;
  file: File | null;
  onUploadProgress: (progressEvent: AxiosProgressEvent) => void;
  onUploadError: (error: unknown) => void;
};

type UploadFileResult = Promise<{ fileKey: string; value: string } | void>;

const useComponentFileUpload = () => {
  const { accessToken } = useAuth();
  const { showNotification } = useNotificationContext();

  const uploadFile = async (args: UploadFileArgs): UploadFileResult => {
    const {
      procId,
      file: originalFile,
      onUploadProgress,
      onUploadError,
    } = args;

    if (!procId || !originalFile) return;

    try {
      const file = renameFile(
        originalFile,
        encodeURIComponent(originalFile.name),
      );
      const formData = new FormData();
      formData.append('file', file);

      // TODO: replace axios by openapi with tracking progress mechanism
      const { data } = await api.post(
        `/procs/${procId}/file-upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress,
        },
      );
      return { value: data.url, fileKey: data.fileKey };
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Unknown error while component file uploading';

      onUploadError(error);
      showNotification({
        type: 'error',
        message: 'Error uploading video',
        description: errorMessage,
      });
      captureError(
        `Error uploading file for component in proc ${procId}: ${errorMessage}`,
      );
    }
  };

  return { uploadFile };
};

export default useComponentFileUpload;
